import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/states';
import { useCreateJob } from '@/routes/customers/useCustomerActions';
import { useLeadSources } from '@/routes/leadSources/useLeadSources';
import type { Property } from '@/routes/customers/useCustomers';

/**
 * "New job" — the other quick action the AC asks for (M1-04).
 *
 * M1-06 built the create-job endpoint with the AC "a job can be created from a
 * customer or a property", and shipped without a screen to do it from. This is
 * that screen, in its smallest honest form: the customer is already known, so
 * the questions are which address, what is wrong, and how urgent.
 *
 * <b>It creates the job unscheduled.</b> Scheduling is a dispatch decision made
 * on the board against everyone else's day, and a date field here would invite
 * someone to make it blind — the job lands in the board's unassigned rail,
 * which is exactly where M1-09 put "so nothing falls through the cracks".
 * `POST /jobs` derives Unscheduled from the absent start, so this is the API's
 * own default rather than a client-side convention.
 *
 * <b>Priority is sent explicitly, never omitted.</b> `Low` is ordinal 0, so an
 * omitted field would bind to Low rather than the domain's Normal default
 * (JobDtos.cs). The select therefore always has a value.
 */
export function NewJobDialog({
  customerId,
  properties,
  defaultLeadSourceId,
  defaultLeadSourceDetail,
  onClose,
  onCreated,
}: {
  customerId: string;
  /** Active properties only — a job cannot be booked at an archived address. */
  properties: readonly Property[];
  /**
   * The CUSTOMER's own attribution, as the starting answer (M1-45).
   *
   * ⚠️ A default, not a copy. Most jobs for a known customer did come in the
   * way that customer always comes in, so pre-filling saves the office a step
   * and stops the field being skipped. But a repeat customer calling from a
   * different ad this time is the case the per-job field exists for, so this is
   * always overridable — including back to "not recorded".
   */
  defaultLeadSourceId?: string | null;
  defaultLeadSourceDetail?: string | null;
  onClose: () => void;
  onCreated: (description: string) => void;
}) {
  const { t } = useTranslation();
  const createJob = useCreateJob(customerId);
  const leadSources = useLeadSources();

  const [propertyId, setPropertyId] = useState(properties[0]?.id ?? '');
  const [description, setDescription] = useState('');
  const [jobType, setJobType] = useState('');
  const [priority, setPriority] = useState<'Low' | 'Normal' | 'High' | 'Emergency'>('Normal');
  // ⚠️ Seeded from the customer only if that source is still ACTIVE. A
  // retired one may be KEPT on the record that already carries it, never
  // newly ASSIGNED (LeadSourceEndpoints.ResolveAsync) — defaulting a NEW job to
  // a retired source would build a request the API is bound to refuse, and the
  // office would read the 400 as the dialog being broken.
  const [leadSourceId, setLeadSourceId] = useState(defaultLeadSourceId ?? '');
  const [leadSourceDetail, setLeadSourceDetail] = useState(defaultLeadSourceDetail ?? '');
  const offered = leadSources.data ?? [];
  const seedIsRetired =
    leadSourceId !== '' && offered.length > 0 && !offered.some((s) => s.id === leadSourceId);
  const pickedId = seedIsRetired ? '' : leadSourceId;

  const canSubmit = propertyId !== '' && description.trim() !== '';

  function submit() {
    createJob.mutate(
      {
        propertyId,
        description: description.trim(),
        jobType: jobType.trim() || null,
        priority,
        scheduledStart: null,
        scheduledEnd: null,
        leadSourceId: pickedId === '' ? null : pickedId,
        // Detail without a source is a 400 the API states plainly, so the two
        // travel together or not at all.
        leadSourceDetail: pickedId === '' ? null : leadSourceDetail.trim() || null,
      },
      {
        onSuccess: () => {
          onCreated(description.trim());
          onClose();
        },
      },
    );
  }

  const field =
    'h-9 w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

  return (
    <Dialog
      titleId="new-job-title"
      title={t('customerDetail.newJob.title')}
      description={t('customerDetail.newJob.description')}
      onClose={onClose}
      testId="new-job-dialog"
      dismissLabel={t('customerDetail.newJob.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || createJob.isPending}
            onClick={submit}
          >
            {createJob.isPending
              ? t('customerDetail.newJob.saving')
              : t('customerDetail.newJob.confirm')}
          </Button>
        </>
      }
    >
      {properties.length === 0 ? (
        // A job needs an address. Saying so beats an empty select that looks
        // like it is still loading.
        <EmptyState
          title={t('customerDetail.newJob.noPropertiesTitle')}
          description={t('customerDetail.newJob.noPropertiesDescription')}
        />
      ) : (
        <>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('customerDetail.newJob.property')}</span>
            <select
              className={field}
              value={propertyId}
              onChange={(event) => setPropertyId(event.target.value)}
            >
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.unit
                    ? `${property.address.line1}, ${property.unit}`
                    : property.address.line1}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('customerDetail.newJob.jobType')}</span>
            <input
              className={field}
              value={jobType}
              onChange={(event) => setJobType(event.target.value)}
              placeholder={t('customerDetail.newJob.jobTypePlaceholder')}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t('customerDetail.newJob.jobDescription')}
            </span>
            <textarea
              required
              rows={3}
              className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('customerDetail.newJob.priority')}</span>
            <select
              className={field}
              value={priority}
              onChange={(event) =>
                setPriority(event.target.value as 'Low' | 'Normal' | 'High' | 'Emergency')
              }
            >
              {(['Low', 'Normal', 'High', 'Emergency'] as const).map((option) => (
                <option key={option} value={option}>
                  {t(`common.jobPriority.${option}`, { defaultValue: option })}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('customerDetail.newJob.leadSource')}</span>
            <select
              className={field}
              value={pickedId}
              // Kept, not wiped — see LeadSourceField's own note. `submit`
              // already drops a detail with no source, which is the rule the
              // API states ("Pick a lead source before adding detail about it").
              onChange={(event) => setLeadSourceId(event.target.value)}
            >
              <option value="">{t('customerDetail.newJob.leadSourceUnset')}</option>
              {offered.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">
              {t('customerDetail.newJob.leadSourceDetail')}
            </span>
            <input
              className={field}
              value={leadSourceDetail}
              disabled={pickedId === ''}
              placeholder={t('customerDetail.newJob.leadSourceDetailPlaceholder')}
              onChange={(event) => setLeadSourceDetail(event.target.value)}
            />
          </label>

          <p className="text-xs text-[var(--color-muted-foreground)]">
            {t('customerDetail.newJob.unscheduledNote')}
          </p>
        </>
      )}

      {createJob.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('customerDetail.newJob.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
